// Cosmétiques refaits (choix de Léni, 4 novembre 2026) : chaque pièce du tank prend une couleur libre, une
// matière, un motif et un autocollant ; s'y ajoutent une aura, une traînée et des effets de tir, d'élimination et
// de mort. Les couleurs sont gratuites ; matières, motifs, autocollants et effets se gagnent (coffres en pièces)
// ou s'achètent directement (gemmes). Rien ne change la hitbox, la précision ni la lisibilité des tirs (dossier §9) :
// la couleur du camp reste au cœur de la tourelle, les balles gardent leur couleur, les contours restent sombres.

export type Rarity = 'commun' | 'rare' | 'epique' | 'legendaire' | 'mythique';
export const RARITIES: Rarity[] = ['commun', 'rare', 'epique', 'legendaire', 'mythique'];
export const RARITY_NAME: Record<Rarity, string> = { commun: 'Commun', rare: 'Rare', epique: 'Épique', legendaire: 'Légendaire', mythique: 'Mythique' };
export const RARITY_COLOR: Record<Rarity, number> = {
  commun: 0x9aa6b2,
  rare: 0x4aa3ff,
  epique: 0xb26bff,
  legendaire: 0xffb340,
  mythique: 0xff4f7b,
};

export type PartId = 'hull' | 'treads' | 'turret' | 'barrels';
export const PARTS: PartId[] = ['hull', 'treads', 'turret', 'barrels'];
/**
 * Pièces qui reçoivent un autocollant : les chenilles, seule partie large et bien visible sur les côtés (la caisse est
 * presque entièrement sous le dôme, le dôme garde la couleur du camp, les tubes restent lisibles).
 */
export const DECAL_PARTS: PartId[] = ['treads'];

export type CosmeticSlot = 'mat' | 'pat' | 'dec' | 'aura' | 'trail' | 'shot' | 'kill' | 'death';

/**
 * Collections à thème (choix de Léni, 5 novembre 2026) : chaque collection assortit matière, motif, autocollant et
 * effets. « Origine » regroupe les objets de départ. L'ordre sert à l'affichage (Atelier, planche).
 */
export const COLLECTIONS = ['origine', 'banquise', 'volcan', 'cyber', 'jungle', 'abysses', 'cosmos', 'empire', 'ferraille', 'spectre', 'sakura', 'desert', 'celeste', 'noel'] as const;
export type CollectionId = (typeof COLLECTIONS)[number];

export interface CosmeticDef {
  id: string;
  slot: CosmeticSlot;
  name: string;
  rarity: Rarity;
  collection: CollectionId;
  /**
   * Saison dont le pass donne cet objet (choix de Léni, 5 novembre 2026) : exclusif au pass, jamais dans les coffres
   * ni en achat direct, gardé à vie dans l'inventaire. Absent pour les objets de la boutique.
   */
  season?: string;
}

const def = (slot: CosmeticSlot, key: string, name: string, rarity: Rarity, collection: CollectionId = 'origine', season?: string): CosmeticDef =>
  season ? { id: `${slot}:${key}`, slot, name, rarity, collection, season } : { id: `${slot}:${key}`, slot, name, rarity, collection };
/** Objet de la collection « Noël », exclusif au pass de la saison 2 « L’ATELIER DU PÔLE ». */
const noel = (slot: CosmeticSlot, key: string, name: string, rarity: Rarity): CosmeticDef => def(slot, key, name, rarity, 'noel', 'S2');

/** Matières : la façon dont la couleur choisie est rendue. « Mat » est gratuite. */
export const MATERIALS = [
  def('mat', 'mat', 'Mat', 'commun'),
  def('mat', 'satin', 'Satin', 'commun'),
  def('mat', 'brosse', 'Métal brossé', 'rare'),
  def('mat', 'carbone', 'Carbone', 'rare'),
  def('mat', 'chrome', 'Chrome', 'epique'),
  def('mat', 'nacre', 'Nacre', 'epique'),
  def('mat', 'or', 'Or', 'legendaire'),
  def('mat', 'neon', 'Néon', 'legendaire'),
  def('mat', 'magma', 'Magma', 'mythique'),
  def('mat', 'glace', 'Glace', 'epique', 'banquise'),
  def('mat', 'obsidienne', 'Obsidienne', 'rare', 'volcan'),
  def('mat', 'holo', 'Holographique', 'legendaire', 'cyber'),
  def('mat', 'bois', 'Bois', 'commun', 'jungle'),
  def('mat', 'abysse', 'Bioluminescence', 'epique', 'abysses'),
  def('mat', 'galaxie', 'Galaxie', 'mythique', 'cosmos'),
  def('mat', 'marbre', 'Marbre', 'rare', 'empire'),
  def('mat', 'rouille', 'Rouille', 'commun', 'ferraille'),
  def('mat', 'spectral', 'Spectral', 'legendaire', 'spectre'),
  def('mat', 'laque', 'Laque', 'epique', 'sakura'),
  def('mat', 'gres', 'Grès', 'commun', 'desert'),
  def('mat', 'porcelaine', 'Porcelaine', 'rare', 'celeste'),
  noel('mat', 'paindepices', 'Pain d’épices', 'legendaire'),
  noel('mat', 'velours', 'Velours rouge', 'epique'),
  noel('mat', 'boulenoel', 'Verre de Noël', 'mythique'),
];

export const PATTERNS = [
  def('pat', 'rayures', 'Rayures', 'commun'),
  def('pat', 'damier', 'Damier', 'commun'),
  def('pat', 'camo', 'Camouflage', 'rare'),
  def('pat', 'hexa', 'Hexagones', 'rare'),
  def('pat', 'ecailles', 'Écailles', 'epique'),
  def('pat', 'circuit', 'Circuit', 'epique'),
  def('pat', 'tigre', 'Tigre', 'legendaire'),
  def('pat', 'flammes', 'Flammes', 'legendaire'),
  def('pat', 'givre', 'Givre', 'rare', 'banquise'),
  def('pat', 'lave', 'Coulées de lave', 'epique', 'volcan'),
  def('pat', 'pixels', 'Pixels', 'commun', 'cyber'),
  def('pat', 'cascade', 'Code en cascade', 'rare', 'cyber'),
  def('pat', 'feuillage', 'Feuillage', 'rare', 'jungle'),
  def('pat', 'courants', 'Courants', 'commun', 'abysses'),
  def('pat', 'constellations', 'Constellations', 'rare', 'cosmos'),
  def('pat', 'losanges', 'Losanges', 'commun', 'empire'),
  def('pat', 'plaques', 'Plaques rivetées', 'rare', 'ferraille'),
  def('pat', 'toiles', 'Toiles d’araignée', 'rare', 'spectre'),
  def('pat', 'seigaiha', 'Vagues', 'rare', 'sakura'),
  def('pat', 'dunes', 'Dunes', 'commun', 'desert'),
  def('pat', 'plumes', 'Plumes', 'epique', 'celeste'),
  noel('pat', 'sucredorge', 'Sucre d’orge', 'rare'),
  noel('pat', 'tricot', 'Pull de Noël', 'epique'),
  noel('pat', 'emballage', 'Papier cadeau', 'epique'),
];

export const DECALS = [
  def('dec', 'etoile', 'Étoile', 'commun'),
  def('dec', 'chevrons', 'Chevrons', 'commun'),
  def('dec', 'cible', 'Cible', 'commun'),
  def('dec', 'eclair', 'Éclair', 'rare'),
  def('dec', 'flamme', 'Flamme', 'rare'),
  def('dec', 'griffe', 'Griffes', 'rare'),
  def('dec', 'ailes', 'Ailes', 'epique'),
  def('dec', 'couronne', 'Couronne', 'epique'),
  def('dec', 'crane', 'Crâne', 'legendaire'),
  def('dec', 'dragon', 'Dragon', 'mythique'),
  def('dec', 'flocon', 'Flocon', 'commun', 'banquise'),
  def('dec', 'volcan', 'Volcan', 'commun', 'volcan'),
  def('dec', 'codebarre', 'Code-barres', 'commun', 'cyber'),
  def('dec', 'feuille', 'Feuille', 'commun', 'jungle'),
  def('dec', 'ancre', 'Ancre', 'commun', 'abysses'),
  def('dec', 'pieuvre', 'Pieuvre', 'epique', 'abysses'),
  def('dec', 'planete', 'Planète', 'rare', 'cosmos'),
  def('dec', 'fusee', 'Fusée', 'commun', 'cosmos'),
  def('dec', 'blason', 'Blason', 'rare', 'empire'),
  def('dec', 'epees', 'Épées croisées', 'commun', 'empire'),
  def('dec', 'danger', 'Danger', 'commun', 'ferraille'),
  def('dec', 'citrouille', 'Citrouille', 'rare', 'spectre'),
  def('dec', 'sakura', 'Fleur de cerisier', 'commun', 'sakura'),
  def('dec', 'scarabee', 'Scarabée', 'rare', 'desert'),
  def('dec', 'aureole', 'Auréole', 'rare', 'celeste'),
  noel('dec', 'sapin', 'Sapin', 'commun'),
  noel('dec', 'renne', 'Renne', 'rare'),
  noel('dec', 'etoilenoel', 'Étoile de Noël', 'rare'),
  noel('dec', 'houx', 'Houx', 'epique'),
];

export const AURAS = [
  def('aura', 'etincelles', 'Étincelles', 'rare'),
  def('aura', 'flocons', 'Flocons', 'rare'),
  def('aura', 'braises', 'Braises', 'epique'),
  def('aura', 'lucioles', 'Lucioles', 'epique'),
  def('aura', 'orage', 'Orage', 'legendaire'),
  def('aura', 'runes', 'Anneau de runes', 'mythique'),
  def('aura', 'blizzard', 'Blizzard', 'legendaire', 'banquise'),
  def('aura', 'cendres', 'Cendres', 'rare', 'volcan'),
  def('aura', 'donnees', 'Flux de données', 'epique', 'cyber'),
  def('aura', 'papillons', 'Papillons', 'legendaire', 'jungle'),
  def('aura', 'meduses', 'Méduses', 'legendaire', 'abysses'),
  def('aura', 'satellites', 'Satellites', 'epique', 'cosmos'),
  def('aura', 'tresor', 'Trésor', 'legendaire', 'empire'),
  def('aura', 'radioactif', 'Radioactif', 'epique', 'ferraille'),
  def('aura', 'ames', 'Âmes errantes', 'mythique', 'spectre'),
  def('aura', 'lanternes', 'Lanternes', 'epique', 'sakura'),
  def('aura', 'sable', 'Tempête de sable', 'epique', 'desert'),
  def('aura', 'halo', 'Halo divin', 'mythique', 'celeste'),
  noel('aura', 'guirlande', 'Guirlande lumineuse', 'rare'),
  noel('aura', 'boules', 'Boules de Noël', 'epique'),
  noel('aura', 'etoilepolaire', 'Étoile polaire', 'mythique'),
];

export const TRAILS = [
  def('trail', 'poussiere', 'Poussière', 'commun'),
  def('trail', 'lumiere', 'Sillage lumineux', 'rare'),
  def('trail', 'bulles', 'Bulles', 'rare'),
  def('trail', 'flammes', 'Flammes', 'epique'),
  def('trail', 'etoiles', 'Poussière d’étoiles', 'legendaire'),
  def('trail', 'arcenciel', 'Arc-en-ciel', 'mythique'),
  def('trail', 'givre', 'Traînée de givre', 'rare', 'banquise'),
  def('trail', 'lave', 'Lave', 'epique', 'volcan'),
  def('trail', 'glitch', 'Glitch', 'rare', 'cyber'),
  def('trail', 'feuilles', 'Feuilles', 'rare', 'jungle'),
  def('trail', 'ecume', 'Écume', 'rare', 'abysses'),
  def('trail', 'comete', 'Comète', 'legendaire', 'cosmos'),
  def('trail', 'tapis', 'Tapis rouge', 'epique', 'empire'),
  def('trail', 'echappement', 'Gaz d’échappement', 'commun', 'ferraille'),
  def('trail', 'brume', 'Brume', 'rare', 'spectre'),
  def('trail', 'sakura', 'Pétales de cerisier', 'legendaire', 'sakura'),
  def('trail', 'sable', 'Sable', 'commun', 'desert'),
  def('trail', 'plumes', 'Plumes', 'epique', 'celeste'),
  noel('trail', 'neige', 'Neige fraîche', 'commun'),
  noel('trail', 'paillettes', 'Paillettes dorées', 'epique'),
  noel('trail', 'traineau', 'Sillage de traîneau', 'legendaire'),
];

export const SHOTS = [
  def('shot', 'etincelles', 'Gerbe d’étincelles', 'commun'),
  def('shot', 'fumee', 'Fumée de poudre', 'rare'),
  def('shot', 'plasma', 'Anneau de plasma', 'epique'),
  def('shot', 'etoile', 'Éclat d’étoile', 'legendaire'),
  def('shot', 'cristaux', 'Cristaux', 'commun', 'banquise'),
  def('shot', 'eruption', 'Éruption', 'rare', 'volcan'),
  def('shot', 'laser', 'Laser', 'rare', 'cyber'),
  def('shot', 'pollen', 'Pollen', 'commun', 'jungle'),
  def('shot', 'encre', 'Jet d’encre', 'rare', 'abysses'),
  def('shot', 'pulsar', 'Pulsar', 'epique', 'cosmos'),
  def('shot', 'fanfare', 'Fanfare', 'rare', 'empire'),
  def('shot', 'petarade', 'Pétarade', 'rare', 'ferraille'),
  def('shot', 'feufollet', 'Feu follet', 'epique', 'spectre'),
  def('shot', 'eventail', 'Éventail', 'rare', 'sakura'),
  def('shot', 'soleil', 'Éclat solaire', 'epique', 'desert'),
  def('shot', 'rayon', 'Rayon de lumière', 'legendaire', 'celeste'),
  noel('shot', 'clochettes', 'Clochettes', 'rare'),
  noel('shot', 'papillotes', 'Papillote', 'epique'),
  noel('shot', 'etoilefilante', 'Étoile filante', 'legendaire'),
];

export const KILLS = [
  def('kill', 'confettis', 'Confettis', 'rare'),
  def('kill', 'onde', 'Onde de choc', 'epique'),
  def('kill', 'foudre', 'Foudre', 'legendaire'),
  def('kill', 'supernova', 'Supernova', 'mythique'),
  def('kill', 'gel', 'Gel instantané', 'epique', 'banquise'),
  def('kill', 'meteores', 'Pluie de météores', 'legendaire', 'volcan'),
  def('kill', 'pixelisation', 'Pixelisation', 'epique', 'cyber'),
  def('kill', 'floraison', 'Floraison', 'epique', 'jungle'),
  def('kill', 'tourbillon', 'Tourbillon', 'epique', 'abysses'),
  def('kill', 'trounoir', 'Trou noir', 'mythique', 'cosmos'),
  def('kill', 'pluiedor', 'Pluie d’or', 'mythique', 'empire'),
  def('kill', 'boulons', 'Boulons', 'rare', 'ferraille'),
  def('kill', 'chauvesouris', 'Chauves-souris', 'legendaire', 'spectre'),
  def('kill', 'hanabi', 'Feu d’artifice', 'mythique', 'sakura'),
  def('kill', 'tornade', 'Tornade', 'legendaire', 'desert'),
  def('kill', 'jugement', 'Jugement', 'mythique', 'celeste'),
  noel('kill', 'cadeau', 'Cadeau surprise', 'rare'),
  noel('kill', 'nouvelan', 'Feux du Nouvel An', 'legendaire'),
  noel('kill', 'avalanche', 'Avalanche', 'legendaire'),
];

export const DEATHS = [
  def('death', 'eclats', 'Éclats de blindage', 'rare'),
  def('death', 'fumee', 'Fumée noire', 'rare'),
  def('death', 'implosion', 'Implosion', 'epique'),
  def('death', 'vaporisation', 'Vaporisation', 'legendaire'),
  def('death', 'brisglace', 'Bris de glace', 'rare', 'banquise'),
  def('death', 'fusion', 'Fusion', 'epique', 'volcan'),
  def('death', 'deconnexion', 'Déconnexion', 'legendaire', 'cyber'),
  def('death', 'racines', 'Racines', 'rare', 'jungle'),
  def('death', 'naufrage', 'Naufrage', 'rare', 'abysses'),
  def('death', 'nebuleuse', 'Nébuleuse', 'legendaire', 'cosmos'),
  def('death', 'statue', 'Statue brisée', 'epique', 'empire'),
  def('death', 'ferraille', 'Tas de ferraille', 'epique', 'ferraille'),
  def('death', 'fantome', 'Fantôme', 'epique', 'spectre'),
  def('death', 'encre', 'Tache d’encre', 'rare', 'sakura'),
  def('death', 'sablier', 'Sablier', 'mythique', 'desert'),
  def('death', 'ascension', 'Ascension', 'legendaire', 'celeste'),
  noel('death', 'bouledeneige', 'Boule de neige', 'epique'),
  noel('death', 'bonhomme', 'Bonhomme de neige', 'legendaire'),
];

export const COSMETIC_DEFS: CosmeticDef[] = [...MATERIALS, ...PATTERNS, ...DECALS, ...AURAS, ...TRAILS, ...SHOTS, ...KILLS, ...DEATHS];
export const COSMETIC_DEF: Record<string, CosmeticDef> = Object.fromEntries(COSMETIC_DEFS.map((d) => [d.id, d]));
/** Objets possédés par tout le monde dès le départ. */
export const FREE_COSMETICS = new Set(['mat:mat']);

export interface PartPaint {
  /** Couleur libre (0xRRGGBB). */
  color: number;
  /** Matière (identifiant `mat:*`). */
  mat: string;
  /** Motif (`pat:*`) ou aucun, et sa couleur. */
  pat: string | null;
  patColor: number;
  /** Autocollant (`dec:*`) ou aucun, et sa couleur ; seulement sur DECAL_PARTS. */
  dec: string | null;
  decColor: number;
}

export interface TankLook {
  parts: Record<PartId, PartPaint>;
  aura: string | null;
  trail: string | null;
  shot: string | null;
  kill: string | null;
  death: string | null;
}

const plain = (color: number): PartPaint => ({ color, mat: 'mat:mat', pat: null, patColor: 0xb8c2cc, dec: null, decColor: 0xeef3f8 });

/** Apparence par défaut : celle du style Atelier d'aujourd'hui. */
export const DEFAULT_LOOK: TankLook = {
  parts: { hull: plain(0x8b97a5), treads: plain(0x3a414b), turret: plain(0x8b97a5), barrels: plain(0x5f6976) },
  aura: null,
  trail: null,
  shot: null,
  kill: null,
  death: null,
};

const isColor = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 0xffffff;

/**
 * Plancher de luminosité par pièce (canal le plus fort) : un tank peint presque noir se confondrait avec le sol sombre.
 * Les chenilles peuvent être plus sombres (elles le sont par défaut). Une couleur trop sombre est éclaircie en gardant
 * sa teinte.
 */
export const MIN_BRIGHT: Record<PartId, number> = { hull: 0x46, treads: 0x26, turret: 0x46, barrels: 0x3a };
export function brightEnough(color: number, part: PartId): number {
  const r = (color >> 16) & 255;
  const g = (color >> 8) & 255;
  const b = color & 255;
  const m = Math.max(r, g, b);
  const min = MIN_BRIGHT[part];
  if (m >= min) return color;
  if (m === 0) return (min << 16) | (min << 8) | min;
  const k = min / m;
  return (Math.round(r * k) << 16) | (Math.round(g * k) << 8) | Math.round(b * k);
}
const ofSlot = (v: unknown, slot: CosmeticSlot): v is string => typeof v === 'string' && COSMETIC_DEF[v]?.slot === slot;

/**
 * Apparence valide à partir de données quelconques (client, base) : valeurs inconnues remplacées par celles par défaut,
 * objets non possédés retirés quand `owns` est fourni. Le serveur s'en sert avant d'enregistrer ou de diffuser.
 */
export function sanitizeLook(raw: unknown, owns?: (id: string) => boolean): TankLook {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const ok = (id: string) => FREE_COSMETICS.has(id) || !owns || owns(id);
  const pick = (v: unknown, slot: CosmeticSlot): string | null => (ofSlot(v, slot) && ok(v) ? v : null);
  const partsIn = (o.parts && typeof o.parts === 'object' ? o.parts : {}) as Record<string, Record<string, unknown> | undefined>;
  const parts = {} as Record<PartId, PartPaint>;
  for (const id of PARTS) {
    const p = partsIn[id] ?? {};
    const d = DEFAULT_LOOK.parts[id];
    parts[id] = {
      color: brightEnough(isColor(p.color) ? p.color : d.color, id),
      mat: pick(p.mat, 'mat') ?? d.mat,
      pat: pick(p.pat, 'pat'),
      patColor: isColor(p.patColor) ? p.patColor : d.patColor,
      dec: DECAL_PARTS.includes(id) ? pick(p.dec, 'dec') : null,
      decColor: isColor(p.decColor) ? p.decColor : d.decColor,
    };
  }
  return { parts, aura: pick(o.aura, 'aura'), trail: pick(o.trail, 'trail'), shot: pick(o.shot, 'shot'), kill: pick(o.kill, 'kill'), death: pick(o.death, 'death') };
}

/** Clé stable d'une apparence (cache des images de tank côté client, comparaison côté serveur). */
export function lookKey(l: TankLook): string {
  return JSON.stringify([PARTS.map((p) => [l.parts[p].color, l.parts[p].mat, l.parts[p].pat, l.parts[p].patColor, l.parts[p].dec, l.parts[p].decColor]), l.aura, l.trail, l.shot, l.kill, l.death]);
}

// ─── Coffres et prix (choix de Léni, 4 novembre 2026) ───────────────────

export type ChestId = 'bronze' | 'argent' | 'or';

export interface ChestDef {
  id: ChestId;
  name: string;
  /** Prix en pièces (jamais en gemmes : pas de hasard contre de l'argent réel). */
  coins: number;
  /** Chances par rareté, en pour mille (somme = 1000). */
  odds: Record<Rarity, number>;
}

export const CHESTS: ChestDef[] = [
  { id: 'bronze', name: 'Coffre de bronze', coins: 1000, odds: { commun: 640, rare: 280, epique: 60, legendaire: 18, mythique: 2 } },
  { id: 'argent', name: 'Coffre d’argent', coins: 3000, odds: { commun: 300, rare: 400, epique: 220, legendaire: 70, mythique: 10 } },
  { id: 'or', name: 'Coffre d’or', coins: 6000, odds: { commun: 0, rare: 0, epique: 700, legendaire: 250, mythique: 50 } },
];
export const CHEST_BY_ID: Record<string, ChestDef> = Object.fromEntries(CHESTS.map((c) => [c.id, c]));

/** Pièces rendues pour un objet déjà possédé. */
export const DUPLICATE_REFUND: Record<Rarity, number> = { commun: 300, rare: 600, epique: 1200, legendaire: 2500, mythique: 5000 };

/** Achat direct, sans hasard, en gemmes. */
export const GEM_PRICE: Record<Rarity, number> = { commun: 60, rare: 120, epique: 220, legendaire: 350, mythique: 600 };

/** Objet exclusif au pass d'une saison : ni coffre ni achat direct en gemmes. */
export const isSeasonItem = (id: string): boolean => Boolean(COSMETIC_DEF[id]?.season);

/** Objets qu'un coffre peut donner (tout sauf ce qui est gratuit et les objets exclusifs au pass). */
export const CHEST_POOL: CosmeticDef[] = COSMETIC_DEFS.filter((d) => !FREE_COSMETICS.has(d.id) && !d.season);

/** Rareté tirée par un coffre, à partir d'un entier `roll` tiré uniformément dans [0, 1000) par l'appelant. */
export function chestRarity(chest: ChestDef, roll: number): Rarity {
  let acc = 0;
  for (const r of RARITIES) {
    acc += chest.odds[r];
    if (roll < acc) return r;
  }
  throw new Error(`chances du coffre ${chest.id} : total ${acc} ‰ au lieu de 1000`);
}
for (const c of CHESTS) if (RARITIES.reduce((s, r) => s + c.odds[r], 0) !== 1000) throw new Error(`chances du coffre ${c.id} : total différent de 1000 ‰`);

/** Objets d'une rareté que les coffres peuvent donner. */
export function poolOf(rarity: Rarity): CosmeticDef[] {
  return CHEST_POOL.filter((d) => d.rarity === rarity);
}
